// ─────────────────────────────────────────────────────────────────────────────
// Mock Predictive Intelligence Data
// Used in: usePredictive.js hooks when USE_MOCK = true
// ─────────────────────────────────────────────────────────────────────────────

// Revenue Forecast — next month prediction
export const mockRevenueForecast = {
  predicted_revenue: 245000,
  forecast_month: "Mar 2025",
  trend: "growing",
  confidence_score: 72,
  method: "weighted_ensemble",
  monthly_data: [
    { month: "Sep", year: 2024, revenue: 185000 },
    { month: "Oct", year: 2024, revenue: 198000 },
    { month: "Nov", year: 2024, revenue: 210000 },
    { month: "Dec", year: 2024, revenue: 195000 },
    { month: "Jan", year: 2025, revenue: 228000 },
    { month: "Feb", year: 2025, revenue: 238000 },
  ],
  details: {
    data_points_used: 6,
    moving_average_prediction: 240000,
    regression_prediction: 252000,
    growth_rate_prediction: 243000,
    growth_rate_percent: 4.4,
    regression_slope: 8200,
    std_deviation: 15200,
  },
};

// Extended Revenue Forecast — multi-month prediction
export const mockRevenueForecastExtended = {
  forecasts: [
    { month: "Mar 2025", predicted_revenue: 245000, confidence_score: 72 },
    { month: "Apr 2025", predicted_revenue: 258000, confidence_score: 65 },
    { month: "May 2025", predicted_revenue: 271000, confidence_score: 58 },
    { month: "Jun 2025", predicted_revenue: 285000, confidence_score: 51 },
    { month: "Jul 2025", predicted_revenue: 292000, confidence_score: 45 },
    { month: "Aug 2025", predicted_revenue: 305000, confidence_score: 40 },
  ],
  trend: "growing",
  base_data: [
    { month: "Sep", year: 2024, revenue: 185000 },
    { month: "Oct", year: 2024, revenue: 198000 },
    { month: "Nov", year: 2024, revenue: 210000 },
    { month: "Dec", year: 2024, revenue: 195000 },
    { month: "Jan", year: 2025, revenue: 228000 },
    { month: "Feb", year: 2025, revenue: 238000 },
  ],
  summary: {
    total_predicted: 1656000,
    avg_confidence: 55,
    trend: "growing",
  },
};

// Dropout Risk — full detail matching backend schema
export const mockDropoutRisk = {
  summary: {
    total: 14,
    critical: 3,
    high: 5,
    medium: 2,
    low: 2,
    new: 2,
  },
  patients: [
    {
      patient_id: 1, patient_name: "Aryan Sharma", risk_level: "critical", risk_score: 88,
      reasons: ["Missed 5 consecutive sessions", "No payment in 30 days"],
      factors: { missed_sessions: 5, total_sessions: 20, missed_ratio: 0.25, last_visit_days: 21, payment_delay_days: 30 },
    },
    {
      patient_id: 2, patient_name: "Diya Kapoor", risk_level: "critical", risk_score: 82,
      reasons: ["Missed 4 sessions", "Declining attendance"],
      factors: { missed_sessions: 4, total_sessions: 15, missed_ratio: 0.27, last_visit_days: 18, payment_delay_days: 15 },
    },
    {
      patient_id: 3, patient_name: "Ishaan Rawat", risk_level: "critical", risk_score: 78,
      reasons: ["Missed 3 sessions", "No follow-up scheduled"],
      factors: { missed_sessions: 3, total_sessions: 12, missed_ratio: 0.25, last_visit_days: 16, payment_delay_days: 22 },
    },
    {
      patient_id: 4, patient_name: "Sneha Verma", risk_level: "high", risk_score: 72,
      reasons: ["Irregular attendance pattern"],
      factors: { missed_sessions: 3, total_sessions: 18, missed_ratio: 0.17, last_visit_days: 14, payment_delay_days: 10 },
    },
    {
      patient_id: 5, patient_name: "Om Rathore", risk_level: "high", risk_score: 68,
      reasons: ["Declined last 2 appointments"],
      factors: { missed_sessions: 2, total_sessions: 10, missed_ratio: 0.20, last_visit_days: 12, payment_delay_days: 5 },
    },
    {
      patient_id: 6, patient_name: "Kavya Joshi", risk_level: "high", risk_score: 65,
      reasons: ["Payment overdue"],
      factors: { missed_sessions: 2, total_sessions: 14, missed_ratio: 0.14, last_visit_days: 10, payment_delay_days: 20 },
    },
    {
      patient_id: 7, patient_name: "Rohan Malhotra", risk_level: "high", risk_score: 62,
      reasons: ["Decreased session frequency"],
      factors: { missed_sessions: 2, total_sessions: 16, missed_ratio: 0.13, last_visit_days: 8, payment_delay_days: 7 },
    },
    {
      patient_id: 8, patient_name: "Ananya Patel", risk_level: "high", risk_score: 60,
      reasons: ["Missed follow-up appointment"],
      factors: { missed_sessions: 1, total_sessions: 8, missed_ratio: 0.13, last_visit_days: 11, payment_delay_days: 12 },
    },
    {
      patient_id: 9, patient_name: "Vikram Singh", risk_level: "medium", risk_score: 50,
      reasons: ["Slightly irregular attendance"],
      factors: { missed_sessions: 2, total_sessions: 20, missed_ratio: 0.10, last_visit_days: 7, payment_delay_days: 0 },
    },
    {
      patient_id: 10, patient_name: "Meera Desai", risk_level: "medium", risk_score: 45,
      reasons: ["One missed session recently"],
      factors: { missed_sessions: 1, total_sessions: 12, missed_ratio: 0.08, last_visit_days: 5, payment_delay_days: 0 },
    },
    {
      patient_id: 11, patient_name: "Aditya Kumar", risk_level: "low", risk_score: 20,
      reasons: ["Regular attendance"],
      factors: { missed_sessions: 0, total_sessions: 24, missed_ratio: 0, last_visit_days: 2, payment_delay_days: 0 },
    },
    {
      patient_id: 12, patient_name: "Priya Nair", risk_level: "low", risk_score: 15,
      reasons: ["Excellent compliance"],
      factors: { missed_sessions: 0, total_sessions: 30, missed_ratio: 0, last_visit_days: 1, payment_delay_days: 0 },
    },
    {
      patient_id: 13, patient_name: "Riya Chandra", risk_level: "new", risk_score: 0,
      reasons: ["Insufficient data for risk evaluation."],
      factors: { missed_sessions: 0, total_sessions: 2, missed_ratio: 0, last_visit_days: 3, payment_delay_days: 0, membership_days: 10 },
      eligibility: { eligible: false, maturity: 'limited_data', message: 'Insufficient data for risk evaluation.', reasons: ['2/3 sessions', '10/14 active days'] },
    },
    {
      patient_id: 14, patient_name: "Arjun Bhatt", risk_level: "new", risk_score: 0,
      reasons: ["No sessions recorded yet."],
      factors: { missed_sessions: 0, total_sessions: 0, missed_ratio: 0, last_visit_days: 0, payment_delay_days: 0, membership_days: 5 },
      eligibility: { eligible: false, maturity: 'no_data', message: 'No sessions recorded yet.', reasons: [] },
    },
  ],
};

// Therapist Performance Rankings
export const mockTherapistPerformance = {
  rankings: [
    {
      therapist_id: 101, therapist_name: "Dr. Priya Sharma",
      sessions_completed: 145, total_sessions: 160, revenue_generated: 87000,
      unique_patients: 42, returning_patients: 38, retention_rate: 90.5,
      cancellation_rate: 3.2, overall_score: 92, tier: "excellent", rank: 1,
      score_breakdown: { session_score: 90, revenue_score: 88, retention_score: 95, cancellation_score: 96 },
    },
    {
      therapist_id: 102, therapist_name: "Dr. Amit Verma",
      sessions_completed: 130, total_sessions: 150, revenue_generated: 78000,
      unique_patients: 38, returning_patients: 32, retention_rate: 84.2,
      cancellation_rate: 5.1, overall_score: 84, tier: "excellent", rank: 2,
      score_breakdown: { session_score: 86, revenue_score: 82, retention_score: 84, cancellation_score: 85 },
    },
    {
      therapist_id: 103, therapist_name: "Dr. Sneha Kapoor",
      sessions_completed: 110, total_sessions: 140, revenue_generated: 66000,
      unique_patients: 30, returning_patients: 22, retention_rate: 73.3,
      cancellation_rate: 8.5, overall_score: 68, tier: "good", rank: 3,
      score_breakdown: { session_score: 78, revenue_score: 70, retention_score: 65, cancellation_score: 58 },
    },
    {
      therapist_id: 104, therapist_name: "Dr. Rajesh Mehta",
      sessions_completed: 85, total_sessions: 120, revenue_generated: 51000,
      unique_patients: 25, returning_patients: 16, retention_rate: 64.0,
      cancellation_rate: 12.3, overall_score: 52, tier: "average", rank: 4,
      score_breakdown: { session_score: 60, revenue_score: 55, retention_score: 52, cancellation_score: 40 },
    },
    {
      therapist_id: 105, therapist_name: "Dr. Neha Gupta",
      sessions_completed: 65, total_sessions: 120, revenue_generated: 39000,
      unique_patients: 20, returning_patients: 12, retention_rate: 60.0,
      cancellation_rate: 15.2, overall_score: 38, tier: "needs_improvement", rank: 5,
      score_breakdown: { session_score: 54, revenue_score: 48, retention_score: 50, cancellation_score: 28 },
    },
    {
      therapist_id: 106, therapist_name: "Dr. Ravi Kumar",
      sessions_completed: 2, total_sessions: 3, revenue_generated: 1800,
      unique_patients: 2, returning_patients: 0, retention_rate: 0,
      cancellation_rate: 0, overall_score: null, tier: "new", rank: 6,
      score_breakdown: null,
      eligibility: { eligible: false, maturity: 'limited_data', message: 'Performance data will appear after sufficient activity.', reasons: ['2/10 sessions completed', '15/30 active days', '2/5 unique patients'] },
    },
  ],
  summary: {
    total_therapists: 6,
    average_score: 67,
    top_performer: "Dr. Priya Sharma",
    tiers: { excellent: 2, good: 1, average: 1, needs_improvement: 1, new: 1 },
  },
};

// Business Advisor — full detail with categories and action routes
export const mockBusinessAdvisor = {
  recommendations: [
    {
      category: "revenue", priority: "critical", title: "Fee Collection Pending",
      message: "12 patients have overdue fees totaling ₹18,500.",
      recommendation: "Send automated payment reminders and consider offering partial payment plans.",
      actionRoute: "/portal/invoice", icon: "wallet",
    },
    {
      category: "demand", priority: "high", title: "Increase Thursday Slots",
      message: "Thursdays have 40% fewer bookings — consider opening more slots.",
      recommendation: "Add 3-4 extra slots on Thursday afternoons to capture unmet demand.",
      actionRoute: "/portal/appointment", icon: "calendar",
    },
    {
      category: "patient_risk", priority: "high", title: "3 Patients at Dropout Risk",
      message: "Critical dropout risk detected for 3 patients who haven't visited in 2+ weeks.",
      recommendation: "Schedule follow-up calls and offer flexible rescheduling options.",
      actionRoute: "/portal/predictive-intelligence/dropout-risk", icon: "alert",
    },
    {
      category: "demand", priority: "medium", title: "Staff Utilization Low",
      message: "Dr. Mehta has only 3 sessions/day vs team average of 6.",
      recommendation: "Review Dr. Mehta's schedule and redistribute appointments for better utilization.",
      actionRoute: "/portal/employeelist", icon: "users",
    },
    {
      category: "revenue", priority: "info", title: "Top Performing Therapy",
      message: "Speech Therapy has the highest patient retention at 92%.",
      recommendation: "Consider promoting Speech Therapy packages and cross-referrals from other departments.",
      actionRoute: "/portal/therapy-intelligence", icon: "chart",
    },
    {
      category: "revenue", priority: "medium", title: "Seasonal Revenue Opportunity",
      message: "Revenue typically dips 15% in April — consider early promotions.",
      recommendation: "Launch a pre-summer therapy package deal to maintain bookings through April.",
      actionRoute: null, icon: "trending",
    },
  ],
  summary: {
    total: 6, critical: 1, high: 2, medium: 2, info: 1,
    categories: {
      revenue: { total: 3, priorities: { critical: 1, medium: 1, info: 1 } },
      demand: { total: 2, priorities: { high: 1, medium: 1 } },
      patient_risk: { total: 1, priorities: { high: 1 } },
    },
  },
  generated_at: new Date().toISOString(),
};

// Data Health — demo clinic's data is seeded clean, so the "no issues found" state
// is the honest mock here (unlike the other predictive pages, this isn't
// hand-curated illustrative content — it reflects real row-level anomalies).
export const mockDataHealth = {
  summary: { total: 0, duplicate_bookings: 0, zero_duration_slots: 0, ledger_drift: 0, total_amount_at_risk: 0 },
  issues: [],
  generated_at: new Date().toISOString(),
};
